import html2canvas from "html2canvas";
import watermark from "static/qca-black.png";


/**
 * This module contains the common core data structures and functions for the dashboard components
 */
export const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Lightweight precomputed date for viewmodel
 */
export interface IStringDate {
	year: string; //e.g "2010"
	monthIndex: number; //zero based index into monthLabels
	dd: string;  //e.g. "01"
	week: string; //e.g. W01
	dateLabel: string;
}
/**
 * DD-MMM-YYYY
 * @param d 
 */
export function dateLabel(d: IStringDate) {
	return d.dd + "-" + monthLabels[d.monthIndex] + "-" + d.year;
}

/**
 * Facility dimension model 
 */
export interface IDimFacility {
	facilityId: number;
	name: string;
	abbrev: string;
	hhsId: number;
}

/**
 * Facility dimension model 
 */
export interface IDimMDT {
	mdtNameId: number;
	name: string;
	nameAndFacility: string;
	facilityId: number;
	hhsId: number;
}

/**
 * Common structure for generic dimensions
 */
export interface IDimension {
	id: number;
	name: string;
}

/**
 * Common / generic dimension 
 */
export type DimensionsById = { [id: number]: IDimension };

/**
 * Generic base plot data
 */
export interface IPlotData {
	/**
	 * Plot labels
	 */
	labels: string[];
	/**
	 * Plot values
	 */
	values: Array<number>;

	name?: string;
}

/**
 * Create a new plot data
 */
export function createPlotData(): IPlotData {
	return {
		labels: [],
		values: [],
	};
}

/**
 * Basic interface for a dictionary<key, accumulator> data source
 */
export interface ISourceData {
	[key: string]: number;
}

/**
 * Create a new basic SourceData object.
 */
export function createData(): ISourceData {
	return {};
}

export type SortOverrideFn = (keys: string[]) => string[];

/**
 * Pivot the source data object into the target plot data object. e.g.:
 * 
 * E.g. Given from = * { key: value, key2: value2 }
 *        pivotObject(from, {}) -> {
*             labels: [key, key2],
*             values: [value, value2]
*         }
 * 
 * @param from Source data
 * @param into Target plot data object
 * @param sortFn optional sort comparer function
 */
export function pivotObject<TSource extends ISourceData, TPlot extends IPlotData>(from: TSource, into: TPlot, sortFn?: (source: TSource, a: string, b: string) => number, sortOverrideFn?: SortOverrideFn): TPlot {
	let ordered: string[] = [];
	let keys = Object.keys(from);

	if (sortOverrideFn != null) {
		ordered = sortOverrideFn(keys);
	} else {
		if (sortFn != null) {
			ordered = keys.sort((a, b) => sortFn(from, a, b));
		} else {
			ordered = keys;
		}
	}

	ordered.forEach(key => {
		into.labels.push(key);
		into.values.push(from[key]);
	});

	return into;
}

/***
 * Try add to the set, returns true if the value was added, false otherwise.
 */
export function addSet<T>(value: T, into: Set<T>) {
	const was = into.size;
	into.add(value);
	return into.size > was;
}

/**
 * Add the value to the key of the object if the key doesn't already exist. 
 * Returns true if the key/value was added, false otherwise.
 * 
 * @param key 
 * @param value 
 * @param into 
 */
export function addKey<TKey>(key: TKey, value: any, into: any) {
	if (into[key] === undefined) {
		into[key] = value;
		return true;
	}

	return false;
}

/**
 * Shared structure for all dashboard view models
 */
export interface IDashboardBaseViewModel {
	referralId: number;
	patientId: any;
	diagnosisId: number;
	uniqueReferral: string;
	uniquePatient: string;
	ageAtDiagnosis: number;
	mdtDate: IStringDate;
	mdt: IDimMDT;
	facility: IDimFacility;
}

export type DashboardBaseFilterFunc<TViewModel extends IDashboardBaseViewModel> = (vm: TViewModel, values: number[] | string[]) => boolean;
export type AddDataBaseFunc<TViewModel extends IDashboardBaseViewModel, TSource extends ISourceData> = (vm: TViewModel, into: TSource) => void;

/**
 * Accumulate the vm value into the source data object.
 * 
 * @param vm source view model
 * @param into data object to accumulate into
 */
export function addDataFactory<TViewModel extends IDashboardBaseViewModel, TSource extends ISourceData>(keyExtractor: (vm: TViewModel) => keyof ISourceData): AddDataBaseFunc<TViewModel, TSource> {
	return (vm: TViewModel, into: TSource) => {
		const key = keyExtractor(vm) as keyof ISourceData;

		if (into[key] == null) {
			(into as any)[key] = 0;
		}
		(into as any)[key] += 1;
	};

}

//default colors can be found at https://plotly.com/javascript/reference/layout/#layout-colorway
const UNKNOWN_COLOR = "#bbd854"; //light green

export const graphColors = {
	unknown: UNKNOWN_COLOR,
	default: ["#1e88e5", "#f79325", "#ffd92f", "#2ca02c", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"],
	//blue, orange, yellow, dark green, red, purple, brown, pink, gray, mustard, pale blue
}

/**
 * Default layout for most charts
 */

/**
 * Common box styling
 * @param height
 */
export function boxStyle(height: number) {
	return {
		minHeight: height,
		maxHeight: height,
		height: height,
		padding: 1,
	};
}
export const defaultStyles = {
	demographicBox: boxStyle(260),
	box: boxStyle,
	defaultBox: boxStyle(260),
	tallBox: boxStyle(460),
	inheritHW: {
		width: "inherit",
		height: "inherit"
	}
};

export const defaultLayout: Partial<Plotly.Layout> = {
	font: { size: 10 },
	margin: {
		l: 80,
		r: 50,
		b: 50,
		t: 10,
		pad: 1
	},
	dragmode: "select" as any,
	colorway: graphColors.default,
};

export function chartAttrs(height: number = 480) {
	//Determine the height of the chart so it can be wrapped in a scrollable container
	const maxHeight = height - 50;
	const layout = Object.assign({}, defaultLayout) as any;

	return {
		outerStyle: { minHeight: maxHeight, maxHeight: maxHeight, height: maxHeight } as React.CSSProperties,
		layout,
	};
}

/**
 * Default chart configuration
 */
export const defaultConfig: Partial<Plotly.Config> = {
	displaylogo: false,
	displayModeBar: false,
	responsive: false,
};

/**
 * Creates a blob
 */

export function downloadFile(content, filename, contentType) {
	// Create a blob
	var blob = new Blob([content], { type: contentType });
	var url = URL.createObjectURL(blob);
	downloadFromURL(url, filename);
}

/**
* Downloads blob as file
*/

export function downloadFromURL(url, filename) {
	// Create a link to download it
	var pom = document.createElement('a');
	pom.href = url;
	pom.setAttribute('download', filename);
	pom.click();
}

/**
 * Creates an image of a div.
 */
export function downloadAsImage(div: HTMLElement, filename: string, isFullScreen: boolean, activeFilters: string) {
	//Clone div 
	const logo = document.createElement('img');
	logo.src = watermark;
	logo.height = 50;
	logo.width = 300;
	logo.style.opacity = "0.3";
	logo.style.position = "absolute";
	logo.style.zIndex = "9999";
	logo.style.top = "10%";
	logo.style.right = "5%";

	const clone = div.cloneNode(true) as HTMLElement;
	clone.id = "clone";
	clone.style.width = (!isFullScreen ? "100%" : "120%");
	clone.className = "";

	const lineBreak = document.createElement('br');



	const currentFilters = document.createElement('div');
	currentFilters.append("Selected Filters: " + activeFilters);
	currentFilters.append(document.createElement('br'));
	currentFilters.append(document.createElement('br'));

	const source = document.createElement('div');
	source.append("Source: Cancer Alliance Queensland (" + window.location.href + ")");
	source.append(document.createElement('br'));
	source.append("Downloaded: " + new Date().toString());
	source.style.fontStyle = 'italic';
	source.style.fontSize = "0.75em"

	const combinedDiv = document.createElement('div');
	combinedDiv.style.width = (Number(getComputedStyle(div).width.slice(0, -2))) + "px";
	combinedDiv.id = 'hiddenClonedDiv';
	combinedDiv.style.position = "relative";

	combinedDiv.append(logo);
	combinedDiv.append(lineBreak);


	combinedDiv.append(clone);

	if (activeFilters != "") {
		combinedDiv.append(currentFilters);
	}
	combinedDiv.append(source);



	document.body.append(combinedDiv);

	//Convert the HTML element to a canvas and then download that canvas 
	html2canvas(combinedDiv, { scale: 1.5 }).then(canvas => {
		const img: any = canvas.toDataURL();
		downloadFromURL(img, filename);
	}).then(_ => combinedDiv.remove())
}
